"use client";

import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Check,
  Download,
  Image as ImageIcon,
  LayoutTemplate,
  LoaderCircle,
  Sparkles,
  Upload,
} from "lucide-react";
import { COVER_IMAGE_PROMPT_MAX_LENGTH } from "@/utils/cover-maker-ai";

type CoverType = "youtube" | "podcast";
type AspectRatio = "landscape" | "portrait" | "square";
type VisualStyle = "photo" | "mono" | "graphic";
type TextAlign = "left" | "center";
type ImageSource = "upload" | "ai";
type GenerationAction = "login" | "credits" | null;
type Locale = "en" | "zh";

type CoverTaskPayload = {
  success?: boolean;
  taskId?: string;
  status?: string;
  progress?: number;
  pollAfterMs?: number;
  code?: string;
  error?: string;
  requestId?: string;
};

type CoverState = {
  title: string;
  subtitle: string;
  coverType: CoverType;
  aspectRatio: AspectRatio;
  style: VisualStyle;
  align: TextAlign;
  accent: string;
  textColor: string;
  overlay: number;
  titleSize: number;
  showGuides: boolean;
  exportSize: "recommended" | "compact";
};

type DrawOptions = CoverState & {
  backgroundUrl: string;
  width: number;
  height: number;
  includeGuides: boolean;
};

const ASSETS: Record<CoverType, string> = {
  youtube: "/images/cover-maker/youtube-studio.webp",
  podcast: "/images/cover-maker/podcast-signal.webp",
};

const PREVIEW_SIZES: Record<AspectRatio, { width: number; height: number }> = {
  landscape: { width: 1280, height: 720 },
  portrait: { width: 720, height: 1280 },
  square: { width: 1000, height: 1000 },
};

const MAX_COVER_TASK_POLLS = 90;

function wait(milliseconds: number) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

async function readCoverTaskPayload(response: Response) {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await response.json().catch(() => ({}))) as CoverTaskPayload;
  }

  const responseText = await response.text().catch(() => "");
  return {
    error:
      responseText && !responseText.trim().startsWith("<")
        ? responseText.slice(0, 300)
        : undefined,
  } satisfies CoverTaskPayload;
}

const DEFAULT_COPY: Record<
  CoverType,
  Pick<CoverState, "title" | "subtitle">
> = {
  youtube: {
    title: "BUILD A SHOW\nPEOPLE REMEMBER",
    subtitle: "From idea to episode",
  },
  podcast: {
    title: "THE\nSIGNAL",
    subtitle: "Ideas worth hearing",
  },
};

const CHINESE_DEFAULT_COPY: Record<
  CoverType,
  Pick<CoverState, "title" | "subtitle">
> = {
  youtube: {
    title: "打造让人\n记住的节目",
    subtitle: "从创意到发布",
  },
  podcast: {
    title: "值得被听见的\n信号",
    subtitle: "关于创意与表达",
  },
};

const LAYOUT_DEFAULTS: Record<
  AspectRatio,
  Pick<CoverState, "align" | "titleSize">
> = {
  landscape: { align: "left", titleSize: 100 },
  portrait: { align: "center", titleSize: 82 },
  square: { align: "center", titleSize: 112 },
};

const AI_PROMPTS: Record<Locale, Record<CoverType, string>> = {
  en: {
    youtube:
      "A confident creator in a modern recording studio, dramatic coral and blue lighting, cinematic editorial photography",
    podcast:
      "A premium studio microphone surrounded by warm analog equipment, intimate editorial photography, refined coral highlights",
  },
  zh: {
    youtube:
      "一位自信的内容创作者坐在现代录音棚中，珊瑚红与蓝色戏剧光线，电影感编辑摄影",
    podcast:
      "高品质录音室麦克风与温暖的模拟设备，亲密的编辑摄影质感，精致珊瑚红高光",
  },
};

function getDefaults(
  coverType: CoverType,
  aspectRatio: AspectRatio,
  language: Locale,
) {
  const localizedCopy =
    language === "zh" ? CHINESE_DEFAULT_COPY[coverType] : DEFAULT_COPY[coverType];
  return {
    ...localizedCopy,
    ...LAYOUT_DEFAULTS[aspectRatio],
  };
}

const copy = {
  en: {
    platform: "Cover type",
    youtube: "YouTube",
    podcast: "Podcast",
    ratio: "Aspect ratio",
    landscape: "16:9",
    portrait: "9:16",
    square: "1:1",
    preview: "Live preview",
    title: "Title",
    subtitle: "Subtitle",
    titlePlaceholder: "Add a short, readable title",
    subtitlePlaceholder: "Optional context",
    image: "Background image",
    uploadMode: "Upload",
    aiMode: "Generate with AI",
    upload: "Upload your image",
    uploadHelp: "JPG, PNG or WebP, up to 15 MB",
    aiPrompt: "Describe the background",
    aiPlaceholder:
      "Describe the subject, setting, lighting, colors, and mood. Typography is added separately.",
    generate: "Generate background",
    generating: "Generating with gpt-image-2",
    generatingHelp: "This usually takes about one minute.",
    aiHelp: "Sign-in required. Uses the current image-generation credit rate.",
    aiModel: "Model: gpt-image-2",
    style: "Visual treatment",
    photo: "Photo",
    mono: "Mono",
    graphic: "Graphic",
    alignment: "Text alignment",
    left: "Left",
    center: "Center",
    titleSize: "Title size",
    contrast: "Image darkening",
    colors: "Colors",
    textColor: "Text",
    accent: "Accent",
    guides: "Show safe-area guides",
    size: "Download size",
    recommendedLandscape: "3840 × 2160 (16:9)",
    compactLandscape: "1280 × 720 (compact 16:9)",
    recommendedPortrait: "1080 × 1920 (9:16)",
    recommendedSquare: "3000 × 3000 (1:1)",
    download: "Download PNG",
    preparing: "Preparing PNG...",
    ready: "Cover downloaded.",
    generatedReady: "AI background generated.",
    generatedWithCredits: "AI background generated. {credits} credits used.",
    uploadError: "Choose a JPG, PNG or WebP image smaller than 15 MB.",
    promptError: "Describe the image in 8 to 10,000 characters.",
    drawError: "The preview could not load. Try another image.",
    generationError:
      "The image could not be generated. Try a shorter, more visual prompt.",
    authError: "Sign in to generate an AI background.",
    creditError: "You do not have enough credits for this image.",
    providerCreditsError:
      "The AI image provider is temporarily out of credits. Please try again later.",
    providerConfigError:
      "The AI image provider configuration needs attention. Please try again later.",
    providerRateError:
      "The AI image provider is busy. Please try again in a minute.",
    providerRejectedError:
      "The AI image provider rejected this request. Try a shorter, more visual prompt.",
    providerTimeoutError:
      "Image generation took too long. Please try again.",
    providerDeliveryError:
      "The generated image could not be retrieved. Please try again.",
    providerUnavailableError:
      "The AI image provider could not complete the request. Please try again.",
    errorReference: "Reference: {id}",
    signIn: "Sign in",
    getCredits: "View credits",
    privacy: "Uploads stay local. AI prompts are sent only when you generate.",
  },
  zh: {
    platform: "封面类型",
    youtube: "YouTube",
    podcast: "Podcast",
    ratio: "画布比例",
    landscape: "16:9",
    portrait: "9:16",
    square: "1:1",
    preview: "实时预览",
    title: "主标题",
    subtitle: "副标题",
    titlePlaceholder: "输入简短、醒目的标题",
    subtitlePlaceholder: "可选补充信息",
    image: "背景图片",
    uploadMode: "上传图片",
    aiMode: "AI 生成",
    upload: "上传你的图片",
    uploadHelp: "支持 JPG、PNG、WebP，最大 15 MB",
    aiPrompt: "描述背景画面",
    aiPlaceholder: "描述主体、场景、光线、色彩和氛围，文字会在编辑器中单独添加。",
    generate: "生成背景图片",
    generating: "正在使用 gpt-image-2 生成",
    generatingHelp: "通常需要大约一分钟。",
    aiHelp: "需要登录，并按当前图片生成价格消耗积分。",
    aiModel: "模型：gpt-image-2",
    style: "视觉效果",
    photo: "原图",
    mono: "黑白",
    graphic: "图形",
    alignment: "文字对齐",
    left: "左对齐",
    center: "居中",
    titleSize: "标题大小",
    contrast: "图片压暗",
    colors: "颜色",
    textColor: "文字",
    accent: "强调色",
    guides: "显示安全区域",
    size: "下载尺寸",
    recommendedLandscape: "3840 × 2160（16:9）",
    compactLandscape: "1280 × 720（轻量 16:9）",
    recommendedPortrait: "1080 × 1920（9:16）",
    recommendedSquare: "3000 × 3000（1:1）",
    download: "下载 PNG",
    preparing: "正在生成 PNG...",
    ready: "封面已下载。",
    generatedReady: "AI 背景图片已生成。",
    generatedWithCredits: "AI 背景图片已生成，已使用 {credits} 积分。",
    uploadError: "请选择小于 15 MB 的 JPG、PNG 或 WebP 图片。",
    promptError: "请使用 8 到 10,000 个字符描述你想要的画面。",
    drawError: "预览加载失败，请换一张图片重试。",
    generationError: "图片生成失败，请尝试更简短、更具体的画面描述。",
    authError: "登录后即可使用 AI 生成背景图片。",
    creditError: "当前积分不足，无法生成这张图片。",
    providerCreditsError: "图片供应商积分暂时不足，请稍后重试。",
    providerConfigError: "图片供应商配置异常，请稍后重试。",
    providerRateError: "图片供应商当前请求较多，请一分钟后重试。",
    providerRejectedError: "图片供应商拒绝了本次请求，请缩短并具体描述画面。",
    providerTimeoutError: "图片生成耗时过长，请重新尝试。",
    providerDeliveryError: "生成的图片暂时无法获取，请重新尝试。",
    providerUnavailableError: "图片供应商未能完成本次请求，请重新尝试。",
    errorReference: "错误编号：{id}",
    signIn: "登录",
    getCredits: "查看积分",
    privacy: "上传图片只在本地处理，点击生成时才会发送 AI 提示词。",
  },
} satisfies Record<Locale, Record<string, string>>;

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function drawCoverImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
  focalX = 0.5,
) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  const x = (width - drawWidth) * focalX;
  const y = (height - drawHeight) / 2;
  context.drawImage(image, x, y, drawWidth, drawHeight);
}

function splitIntoLines(
  context: CanvasRenderingContext2D,
  value: string,
  maxWidth: number,
) {
  const requestedLines = value.trim().split(/\n/);
  const lines: string[] = [];

  for (const requestedLine of requestedLines) {
    const trimmedLine = requestedLine.trim();
    if (!trimmedLine) continue;

    if (!/\s/.test(trimmedLine)) {
      let line = "";
      for (const character of Array.from(trimmedLine)) {
        const testLine = `${line}${character}`;
        if (line && context.measureText(testLine).width > maxWidth) {
          lines.push(line);
          line = character;
        } else {
          line = testLine;
        }
      }
      if (line) lines.push(line);
      continue;
    }

    const words = trimmedLine.split(/\s+/).filter(Boolean);
    let line = words[0];
    for (const word of words.slice(1)) {
      const testLine = `${line} ${word}`;
      if (context.measureText(testLine).width <= maxWidth) {
        line = testLine;
      } else {
        lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }

  return lines.slice(0, 4);
}

async function drawCover(canvas: HTMLCanvasElement, options: DrawOptions) {
  const {
    width,
    height,
    coverType,
    aspectRatio,
    style,
    backgroundUrl,
    overlay,
    accent,
    textColor,
    title,
    subtitle,
    align,
    titleSize,
    includeGuides,
  } = options;
  const context = canvas.getContext("2d");
  if (!context) return;

  canvas.width = width;
  canvas.height = height;
  context.clearRect(0, 0, width, height);
  context.fillStyle = "#151517";
  context.fillRect(0, 0, width, height);

  const sourceWidth = PREVIEW_SIZES[aspectRatio].width;
  const scale = width / sourceWidth;

  if (style === "graphic") {
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#171719");
    gradient.addColorStop(1, "#252329");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    context.save();
    context.globalAlpha = 0.95;
    context.fillStyle = accent;
    context.beginPath();
    context.arc(
      aspectRatio === "landscape" ? width * 0.78 : width * 0.5,
      aspectRatio === "portrait"
        ? height * 0.3
        : aspectRatio === "landscape"
          ? height * 0.26
          : height * 0.66,
      width *
        (aspectRatio === "landscape"
          ? 0.18
          : aspectRatio === "portrait"
            ? 0.36
            : 0.24),
      0,
      Math.PI * 2,
    );
    context.fill();
    context.globalAlpha = 0.75;
    context.fillStyle = "#2f5be7";
    context.fillRect(
      aspectRatio === "landscape" ? width * 0.68 : width * 0.18,
      aspectRatio === "portrait"
        ? height * 0.52
        : aspectRatio === "landscape"
          ? height * 0.62
          : height * 0.52,
      aspectRatio === "landscape" ? width * 0.31 : width * 0.64,
      aspectRatio === "landscape" ? height * 0.16 : height * 0.1,
    );
    context.restore();
  } else {
    const image = await loadImage(backgroundUrl);
    context.save();
    if (style === "mono") {
      context.filter = "grayscale(1) contrast(1.12)";
    }
    const portraitYoutubeStarter =
      aspectRatio === "portrait" &&
      coverType === "youtube" &&
      backgroundUrl.endsWith(ASSETS.youtube);
    drawCoverImage(
      context,
      image,
      width,
      height,
      portraitYoutubeStarter ? 0.72 : 0.5,
    );
    context.restore();
  }

  const shade = Math.min(Math.max(overlay, 0), 0.84);
  if (aspectRatio === "landscape") {
    const shadeGradient = context.createLinearGradient(0, 0, width, 0);
    shadeGradient.addColorStop(
      0,
      `rgba(8, 8, 10, ${Math.min(shade + 0.3, 0.9)})`,
    );
    shadeGradient.addColorStop(0.58, `rgba(8, 8, 10, ${shade * 0.72})`);
    shadeGradient.addColorStop(1, `rgba(8, 8, 10, ${shade * 0.2})`);
    context.fillStyle = shadeGradient;
  } else {
    const shadeGradient = context.createLinearGradient(0, 0, 0, height);
    shadeGradient.addColorStop(
      0,
      `rgba(8, 8, 10, ${aspectRatio === "portrait" ? shade * 0.2 : Math.min(shade + 0.26, 0.88)})`,
    );
    shadeGradient.addColorStop(
      aspectRatio === "portrait" ? 0.42 : 0.48,
      `rgba(8, 8, 10, ${shade * 0.2})`,
    );
    shadeGradient.addColorStop(
      1,
      `rgba(8, 8, 10, ${aspectRatio === "portrait" ? Math.min(shade + 0.26, 0.9) : shade * 0.62})`,
    );
    context.fillStyle = shadeGradient;
  }
  context.fillRect(0, 0, width, height);

  const isCentered = align === "center";
  const safeX =
    (aspectRatio === "landscape"
      ? 78
      : aspectRatio === "portrait"
        ? 58
        : 82) * scale;
  const maxTextWidth =
    (aspectRatio === "landscape"
      ? isCentered
        ? 1080
        : 620
      : aspectRatio === "portrait"
        ? isCentered
          ? 600
          : 570
        : 840) * scale;
  const textX = isCentered ? width / 2 : safeX;
  const titleY =
    (aspectRatio === "landscape"
      ? 205
      : aspectRatio === "portrait"
        ? 650
        : 92) * scale;
  const fontSize = titleSize * scale;
  const lineHeight = fontSize * 0.92;

  context.textAlign = isCentered ? "center" : "left";
  context.textBaseline = "top";
  context.fillStyle = accent;
  const markerWidth =
    (aspectRatio === "landscape"
      ? 74
      : aspectRatio === "portrait"
        ? 70
        : 62) * scale;
  const markerX = isCentered ? textX - markerWidth / 2 : textX;
  context.fillRect(markerX, titleY - 34 * scale, markerWidth, 9 * scale);

  context.font = `900 ${fontSize}px Arial, Helvetica, sans-serif`;
  context.fillStyle = textColor;
  context.shadowColor = "rgba(0,0,0,0.34)";
  context.shadowBlur = 12 * scale;
  context.shadowOffsetY = 4 * scale;

  const lines = splitIntoLines(context, title, maxTextWidth);
  lines.forEach((line, index) => {
    context.fillText(line, textX, titleY + index * lineHeight, maxTextWidth);
  });

  context.shadowColor = "transparent";
  const subtitleSize =
    aspectRatio === "landscape"
      ? 30
      : aspectRatio === "portrait"
        ? 26
        : 34;
  context.font = `700 ${subtitleSize * scale}px Arial, Helvetica, sans-serif`;
  context.fillStyle = textColor;
  context.globalAlpha = 0.88;
  const subtitleY = titleY + lines.length * lineHeight + 28 * scale;
  const visibleSubtitle = subtitle.trim();
  if (visibleSubtitle) {
    context.fillText(visibleSubtitle, textX, subtitleY, maxTextWidth);
  }
  context.globalAlpha = 1;

  if (includeGuides) {
    context.save();
    context.strokeStyle = "rgba(255,255,255,0.72)";
    context.lineWidth = Math.max(2, 2 * scale);
    context.setLineDash([12 * scale, 9 * scale]);
    if (aspectRatio === "portrait") {
      const left = 50 * scale;
      const top = 90 * scale;
      const right = 110 * scale;
      const bottom = 230 * scale;
      context.strokeRect(
        left,
        top,
        width - left - right,
        height - top - bottom,
      );
    } else {
      const inset = (aspectRatio === "landscape" ? 48 : 64) * scale;
      context.strokeRect(inset, inset, width - inset * 2, height - inset * 2);
    }
    context.restore();
  }
}

function sanitizeFileName(value: string) {
  const normalized = value
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
  return normalized || "ariaflow-cover";
}

export default function CoverMakerClient({
  locale = "en",
}: {
  locale?: string;
}) {
  const language: Locale = locale.startsWith("zh") ? "zh" : "en";
  const t = copy[language];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const managedUrlRef = useRef<string | null>(null);
  const [backgroundUrl, setBackgroundUrl] = useState(ASSETS.youtube);
  const [imageSource, setImageSource] = useState<ImageSource>("upload");
  const [aiPrompt, setAiPrompt] = useState(AI_PROMPTS[language].youtube);
  const [generationAction, setGenerationAction] =
    useState<GenerationAction>(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [state, setState] = useState<CoverState>({
    ...getDefaults("youtube", "landscape", language),
    coverType: "youtube",
    aspectRatio: "landscape",
    style: "photo",
    accent: "#ef4343",
    textColor: "#fffaf7",
    overlay: 0.42,
    showGuides: true,
    exportSize: "recommended",
  });

  const previewSize = PREVIEW_SIZES[state.aspectRatio];
  const localizedPrefix = language === "zh" ? `/${locale}` : "";
  const returnPath = `${localizedPrefix}/youtube-thumbnail-maker#cover-editor`;
  const signInHref = `${localizedPrefix}/login?next=${encodeURIComponent(returnPath)}&source=cover-maker`;
  const creditsHref = `${localizedPrefix}/dashboard/credits`;
  const generationFailureMessage = (code?: string) => {
    switch (code) {
      case "PROVIDER_CREDITS_EXHAUSTED":
        return t.providerCreditsError;
      case "PROVIDER_AUTH_FAILED":
      case "PROVIDER_NOT_CONFIGURED":
        return t.providerConfigError;
      case "PROVIDER_RATE_LIMITED":
        return t.providerRateError;
      case "PROVIDER_REQUEST_REJECTED":
        return t.providerRejectedError;
      case "PROVIDER_TIMEOUT":
        return t.providerTimeoutError;
      case "IMAGE_DOWNLOAD_FAILED":
      case "IMAGE_TOO_LARGE":
      case "IMAGE_FORMAT_UNSUPPORTED":
        return t.providerDeliveryError;
      case "PROVIDER_UNAVAILABLE":
      case "PROVIDER_REQUEST_FAILED":
      case "PROVIDER_RESPONSE_INVALID":
      case "PROVIDER_TASK_FAILED":
      case "TASK_STORAGE_FAILED":
      case "TASK_NOT_FOUND":
        return t.providerUnavailableError;
      default:
        return t.generationError;
    }
  };

  const currentOutput = useMemo(() => {
    if (state.aspectRatio === "portrait") {
      return { width: 1080, height: 1920 };
    }
    if (state.aspectRatio === "square") {
      return { width: 3000, height: 3000 };
    }
    if (state.exportSize === "compact") return { width: 1280, height: 720 };
    return { width: 3840, height: 2160 };
  }, [state.aspectRatio, state.exportSize]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let active = true;

    drawCover(canvas, {
      ...state,
      backgroundUrl,
      width: previewSize.width,
      height: previewSize.height,
      includeGuides: state.showGuides,
    }).catch(() => {
      if (active) setError(t.drawError);
    });

    return () => {
      active = false;
    };
  }, [backgroundUrl, previewSize.height, previewSize.width, state, t.drawError]);

  useEffect(
    () => () => {
      if (managedUrlRef.current) URL.revokeObjectURL(managedUrlRef.current);
    },
    [],
  );

  const clearMessages = useCallback(() => {
    setStatus("");
    setError("");
    setGenerationAction(null);
  }, []);

  const updateState = useCallback(
    <K extends keyof CoverState>(key: K, value: CoverState[K]) => {
      setState((current) => ({ ...current, [key]: value }));
      clearMessages();
    },
    [clearMessages],
  );

  const replaceManagedBackground = (url: string | null) => {
    if (managedUrlRef.current) URL.revokeObjectURL(managedUrlRef.current);
    managedUrlRef.current = url;
    if (url) setBackgroundUrl(url);
  };

  const changeCoverType = (coverType: CoverType) => {
    setState((current) => ({
      ...current,
      ...getDefaults(coverType, current.aspectRatio, language),
      coverType,
    }));
    replaceManagedBackground(null);
    setBackgroundUrl(ASSETS[coverType]);
    setAiPrompt(AI_PROMPTS[language][coverType]);
    clearMessages();
  };

  const changeAspectRatio = (aspectRatio: AspectRatio) => {
    setState((current) => ({
      ...current,
      ...LAYOUT_DEFAULTS[aspectRatio],
      aspectRatio,
      exportSize: "recommended",
    }));
    clearMessages();
  };

  const onUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (
      !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      file.size > 15 * 1024 * 1024
    ) {
      setError(t.uploadError);
      event.target.value = "";
      return;
    }

    const url = URL.createObjectURL(file);
    replaceManagedBackground(url);
    setImageSource("upload");
    setState((current) => ({ ...current, style: "photo" }));
    clearMessages();
  };

  const generateBackground = async () => {
    const promptLength = aiPrompt.trim().length;
    if (promptLength < 8 || promptLength > COVER_IMAGE_PROMPT_MAX_LENGTH) {
      setError(t.promptError);
      setGenerationAction(null);
      return;
    }

    setGenerating(true);
    clearMessages();
    try {
      const showGenerationFailure = (
        response: Response,
        payload: CoverTaskPayload,
      ) => {
        if (response.status === 401 || payload.code === "AUTH_REQUIRED") {
          setError(t.authError);
          setGenerationAction("login");
          return;
        }
        if (
          response.status === 403 ||
          payload.code === "INSUFFICIENT_CREDITS"
        ) {
          setError(t.creditError);
          setGenerationAction("credits");
          return;
        }

        const message = payload.code
          ? generationFailureMessage(payload.code)
          : payload.error || t.generationError;
        const referenceId =
          payload.requestId ||
          response.headers.get("X-AriaFlow-Request-Id") ||
          "";
        setError(
          referenceId
            ? `${message} ${t.errorReference.replace("{id}", referenceId)}`
            : message,
        );
      };

      const submitResponse = await fetch("/api/cover-maker/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: aiPrompt,
          coverType: state.coverType,
          aspectRatio: state.aspectRatio,
        }),
      });

      const submitPayload = await readCoverTaskPayload(submitResponse);
      if (!submitResponse.ok) {
        showGenerationFailure(submitResponse, submitPayload);
        return;
      }

      if (!submitPayload.taskId) {
        throw new Error("Image generation did not return a task ID");
      }

      let pollAfterMs = Math.max(0, submitPayload.pollAfterMs || 0);
      for (let attempt = 0; attempt < MAX_COVER_TASK_POLLS; attempt += 1) {
        if (pollAfterMs > 0) await wait(pollAfterMs);

        const statusResponse = await fetch("/api/cover-maker/status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ taskId: submitPayload.taskId }),
        });
        const contentType = statusResponse.headers.get("content-type") || "";

        if (statusResponse.ok && contentType.startsWith("image/")) {
          const blob = await statusResponse.blob();
          const url = URL.createObjectURL(blob);
          replaceManagedBackground(url);
          setImageSource("ai");
          setState((current) => ({ ...current, style: "photo" }));
          const credits = Number(
            statusResponse.headers.get("X-AriaFlow-Credits-Consumed") || "0",
          );
          setStatus(
            credits > 0
              ? t.generatedWithCredits.replace("{credits}", String(credits))
              : t.generatedReady,
          );
          return;
        }

        const statusPayload = await readCoverTaskPayload(statusResponse);
        if (
          statusResponse.ok &&
          ["pending", "processing"].includes(statusPayload.status || "")
        ) {
          pollAfterMs = Math.max(1_000, statusPayload.pollAfterMs || 2_000);
          continue;
        }

        showGenerationFailure(statusResponse, statusPayload);
        return;
      }

      setError(t.providerTimeoutError);
    } catch {
      setError(t.generationError);
    } finally {
      setGenerating(false);
    }
  };

  const downloadCover = async () => {
    setDownloading(true);
    clearMessages();
    try {
      const exportCanvas = document.createElement("canvas");
      await drawCover(exportCanvas, {
        ...state,
        backgroundUrl,
        width: currentOutput.width,
        height: currentOutput.height,
        includeGuides: false,
      });
      const blob = await new Promise<Blob>((resolve, reject) => {
        exportCanvas.toBlob(
          (result) =>
            result ? resolve(result) : reject(new Error("PNG export failed")),
          "image/png",
        );
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${sanitizeFileName(state.title)}-${currentOutput.width}x${currentOutput.height}.png`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setStatus(t.ready);
    } catch {
      setError(t.drawError);
    } finally {
      setDownloading(false);
    }
  };

  const previewClass =
    state.aspectRatio === "landscape"
      ? "aspect-video w-full"
      : state.aspectRatio === "portrait"
        ? "aspect-[9/16] w-full max-w-[420px]"
        : "aspect-square w-full max-w-[660px]";

  return (
    <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-[0_28px_80px_rgba(35,30,28,0.12)]">
      <div className="grid gap-3 border-b border-zinc-200 bg-zinc-50 p-3 sm:grid-cols-2 xl:grid-cols-[0.7fr_1fr_1.1fr] xl:items-end">
        <fieldset>
          <legend className="mb-1.5 text-[11px] font-bold text-zinc-500">
            {t.platform}
          </legend>
          <div
            className="grid grid-cols-2 gap-1 rounded-xl bg-zinc-200/70 p-1"
            role="tablist"
            aria-label={t.platform}
          >
            {(["youtube", "podcast"] as CoverType[]).map((coverType) => (
              <button
                key={coverType}
                type="button"
                role="tab"
                aria-selected={state.coverType === coverType}
                onClick={() => changeCoverType(coverType)}
                className={`min-h-10 rounded-lg px-3 text-sm font-bold transition ${
                  state.coverType === coverType
                    ? "bg-white text-zinc-950 shadow-sm"
                    : "text-zinc-600 hover:bg-white hover:text-zinc-950"
                }`}
              >
                {t[coverType]}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-1.5 text-[11px] font-bold text-zinc-500">
            {t.ratio}
          </legend>
          <div
            className="grid grid-cols-3 gap-1 rounded-xl bg-zinc-200/70 p-1"
            role="tablist"
            aria-label={t.ratio}
          >
            {(["landscape", "portrait", "square"] as AspectRatio[]).map(
              (aspectRatio) => (
                <button
                  key={aspectRatio}
                  type="button"
                  role="tab"
                  aria-selected={state.aspectRatio === aspectRatio}
                  onClick={() => changeAspectRatio(aspectRatio)}
                  className={`min-h-10 rounded-lg px-2 text-sm font-bold transition ${
                    state.aspectRatio === aspectRatio
                      ? "bg-zinc-950 text-white shadow-sm"
                      : "text-zinc-600 hover:bg-white hover:text-zinc-950"
                  }`}
                >
                  {t[aspectRatio]}
                </button>
              ),
            )}
          </div>
        </fieldset>

        <div className="flex items-start gap-2 text-xs font-semibold leading-5 text-zinc-500 sm:col-span-2 xl:col-span-1 xl:items-center xl:pb-2">
          <Check
            className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700 xl:mt-0"
            aria-hidden="true"
          />
          {t.privacy}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1.55fr)_minmax(340px,0.65fr)]">
        <section
          className="relative min-w-0 bg-[#1a191c] p-4 sm:p-7 lg:p-8"
          aria-label={t.preview}
        >
          <div className="mb-4 flex items-center justify-between gap-4 text-xs font-bold text-zinc-400">
            <span>{t.preview}</span>
            <span className="whitespace-nowrap">
              {currentOutput.width} × {currentOutput.height} PNG
            </span>
          </div>
          <div
            className={`relative mx-auto overflow-hidden rounded-xl bg-zinc-900 shadow-[0_28px_70px_rgba(0,0,0,0.42)] ${previewClass}`}
          >
            <canvas
              ref={canvasRef}
              className="h-full w-full object-contain"
              aria-label={
                state.title.trim() ? `${t.preview}: ${state.title}` : t.preview
              }
            />
            {generating && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-950/75 px-6 text-center text-white backdrop-blur-sm"
                role="status"
              >
                <LoaderCircle
                  className="h-8 w-8 animate-spin text-[#ff6b5f]"
                  aria-hidden="true"
                />
                <p className="mt-4 text-sm font-bold">{t.generating}</p>
                <p className="mt-1 text-xs text-zinc-300">
                  {t.generatingHelp}
                </p>
              </div>
            )}
          </div>
        </section>

        <aside
          className="min-w-0 bg-white p-5 sm:p-7"
          aria-label="Cover controls"
        >
          <div className="space-y-6">
            <fieldset>
              <legend className="mb-3 text-sm font-bold text-zinc-950">
                {t.image}
              </legend>
              <div className="mb-3 grid grid-cols-2 rounded-xl bg-zinc-100 p-1">
                {(["upload", "ai"] as ImageSource[]).map((source) => (
                  <button
                    key={source}
                    type="button"
                    onClick={() => {
                      setImageSource(source);
                      clearMessages();
                    }}
                    className={`min-h-10 rounded-lg px-2 text-xs font-bold transition sm:text-sm ${
                      imageSource === source
                        ? "bg-white text-zinc-950 shadow-sm"
                        : "text-zinc-600 hover:text-zinc-950"
                    }`}
                  >
                    {source === "upload" ? t.uploadMode : t.aiMode}
                  </button>
                ))}
              </div>

              {imageSource === "upload" ? (
                <>
                  <label className="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-4 text-sm font-bold text-zinc-800 transition hover:border-[#ef4343] hover:bg-red-50 active:scale-[0.99]">
                    <Upload className="h-4 w-4" aria-hidden="true" />
                    {t.upload}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={onUpload}
                      className="sr-only"
                    />
                  </label>
                  <p className="mt-2 text-xs leading-5 text-zinc-500">
                    {t.uploadHelp}
                  </p>
                </>
              ) : (
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3">
                  <label className="block">
                    <span className="mb-2 flex items-center justify-between gap-3 text-xs font-bold text-zinc-800">
                      {t.aiPrompt}
                      <span className="font-mono text-[10px] font-semibold text-zinc-500">
                        {aiPrompt.length} / {COVER_IMAGE_PROMPT_MAX_LENGTH} ·{" "}
                        {t.aiModel}
                      </span>
                    </span>
                    <textarea
                      value={aiPrompt}
                      onChange={(event) => {
                        setAiPrompt(event.target.value);
                        clearMessages();
                      }}
                      placeholder={t.aiPlaceholder}
                      rows={5}
                      maxLength={COVER_IMAGE_PROMPT_MAX_LENGTH}
                      className="w-full resize-none rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm font-medium leading-6 text-zinc-950 outline-none transition placeholder:text-zinc-400 focus:border-[#ef4343] focus:ring-2 focus:ring-red-100"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={generateBackground}
                    disabled={generating}
                    className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-zinc-950 px-4 text-sm font-bold text-white transition hover:bg-zinc-800 active:translate-y-px disabled:cursor-not-allowed disabled:bg-zinc-400"
                  >
                    {generating ? (
                      <LoaderCircle
                        className="h-4 w-4 animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <Sparkles className="h-4 w-4" aria-hidden="true" />
                    )}
                    {generating ? t.generating : t.generate}
                  </button>
                  <p className="mt-2 text-xs leading-5 text-zinc-500">
                    {t.aiHelp}
                  </p>
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend className="mb-3 text-sm font-bold text-zinc-950">
                {t.style}
              </legend>
              <div className="grid grid-cols-3 gap-2">
                {([
                  ["photo", t.photo, ImageIcon],
                  ["mono", t.mono, LayoutTemplate],
                  ["graphic", t.graphic, LayoutTemplate],
                ] as const).map(([value, label, Icon]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateState("style", value)}
                    className={`flex min-h-12 items-center justify-center gap-2 rounded-xl border px-2 text-xs font-bold transition active:scale-[0.98] ${
                      state.style === value
                        ? "border-zinc-950 bg-zinc-950 text-white"
                        : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-950">
                  {t.title}
                </span>
                <textarea
                  value={state.title}
                  onChange={(event) => updateState("title", event.target.value)}
                  placeholder={t.titlePlaceholder}
                  rows={3}
                  maxLength={90}
                  className="w-full resize-none rounded-xl border border-zinc-300 bg-white px-3.5 py-3 text-sm font-semibold leading-6 text-zinc-950 outline-none transition placeholder:text-zinc-500 focus:border-[#ef4343] focus:ring-2 focus:ring-red-100"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-bold text-zinc-950">
                  {t.subtitle}
                </span>
                <input
                  value={state.subtitle}
                  onChange={(event) =>
                    updateState("subtitle", event.target.value)
                  }
                  placeholder={t.subtitlePlaceholder}
                  maxLength={70}
                  className="min-h-12 w-full rounded-xl border border-zinc-300 bg-white px-3.5 text-sm font-semibold text-zinc-950 outline-none transition placeholder:text-zinc-500 focus:border-[#ef4343] focus:ring-2 focus:ring-red-100"
                />
              </label>
            </div>

            <fieldset>
              <legend className="mb-3 text-sm font-bold text-zinc-950">
                {t.alignment}
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {(["left", "center"] as TextAlign[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => updateState("align", value)}
                    className={`min-h-11 rounded-xl border px-3 text-sm font-bold transition active:scale-[0.98] ${
                      state.align === value
                        ? "border-[#ef4343] bg-red-50 text-red-800"
                        : "border-zinc-200 text-zinc-700 hover:border-zinc-400"
                    }`}
                  >
                    {value === "left" ? t.left : t.center}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="space-y-5">
              <label className="block">
                <span className="mb-2 flex items-center justify-between text-sm font-bold text-zinc-950">
                  {t.titleSize}
                  <span className="font-mono text-xs text-zinc-500">
                    {state.titleSize}px
                  </span>
                </span>
                <input
                  type="range"
                  min="56"
                  max={
                    state.aspectRatio === "landscape"
                      ? "132"
                      : state.aspectRatio === "portrait"
                        ? "112"
                        : "148"
                  }
                  value={state.titleSize}
                  onChange={(event) =>
                    updateState("titleSize", Number(event.target.value))
                  }
                  className="w-full accent-[#ef4343]"
                />
              </label>
              <label className="block">
                <span className="mb-2 flex items-center justify-between text-sm font-bold text-zinc-950">
                  {t.contrast}
                  <span className="font-mono text-xs text-zinc-500">
                    {Math.round(state.overlay * 100)}%
                  </span>
                </span>
                <input
                  type="range"
                  min="0"
                  max="0.75"
                  step="0.05"
                  value={state.overlay}
                  onChange={(event) =>
                    updateState("overlay", Number(event.target.value))
                  }
                  className="w-full accent-[#ef4343]"
                />
              </label>
            </div>

            <fieldset>
              <legend className="mb-3 text-sm font-bold text-zinc-950">
                {t.colors}
              </legend>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex min-h-12 items-center gap-3 rounded-xl border border-zinc-200 px-3 text-xs font-bold text-zinc-700">
                  <input
                    type="color"
                    value={state.textColor}
                    onChange={(event) =>
                      updateState("textColor", event.target.value)
                    }
                    className="h-7 w-7 cursor-pointer rounded border-0 bg-transparent p-0"
                  />
                  {t.textColor}
                </label>
                <label className="flex min-h-12 items-center gap-3 rounded-xl border border-zinc-200 px-3 text-xs font-bold text-zinc-700">
                  <input
                    type="color"
                    value={state.accent}
                    onChange={(event) =>
                      updateState("accent", event.target.value)
                    }
                    className="h-7 w-7 cursor-pointer rounded border-0 bg-transparent p-0"
                  />
                  {t.accent}
                </label>
              </div>
            </fieldset>

            <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-zinc-700">
              <input
                type="checkbox"
                checked={state.showGuides}
                onChange={(event) =>
                  updateState("showGuides", event.target.checked)
                }
                className="h-4 w-4 rounded border-zinc-300 accent-[#ef4343]"
              />
              {t.guides}
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-bold text-zinc-950">
                {t.size}
              </span>
              {state.aspectRatio === "landscape" ? (
                <select
                  value={state.exportSize}
                  onChange={(event) =>
                    updateState(
                      "exportSize",
                      event.target.value as CoverState["exportSize"],
                    )
                  }
                  className="min-h-12 w-full rounded-xl border border-zinc-300 bg-white px-3.5 text-sm font-semibold text-zinc-950 outline-none focus:border-[#ef4343] focus:ring-2 focus:ring-red-100"
                >
                  <option value="recommended">
                    {t.recommendedLandscape}
                  </option>
                  <option value="compact">{t.compactLandscape}</option>
                </select>
              ) : (
                <div className="flex min-h-12 items-center rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 text-sm font-semibold text-zinc-700">
                  {state.aspectRatio === "portrait"
                    ? t.recommendedPortrait
                    : t.recommendedSquare}
                </div>
              )}
            </label>

            {error && (
              <div
                role="alert"
                className="rounded-xl bg-red-50 px-3.5 py-3 text-sm font-semibold text-red-800"
              >
                <p>{error}</p>
                {generationAction && (
                  <a
                    href={
                      generationAction === "login" ? signInHref : creditsHref
                    }
                    className="mt-2 inline-flex underline decoration-red-300 underline-offset-4 hover:text-zinc-950"
                  >
                    {generationAction === "login" ? t.signIn : t.getCredits}
                  </a>
                )}
              </div>
            )}
            <p
              aria-live="polite"
              className="min-h-5 text-sm font-semibold text-emerald-800"
            >
              {status}
            </p>

            <button
              type="button"
              onClick={downloadCover}
              disabled={downloading || generating}
              className="flex min-h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-[#ef4343] px-5 text-sm font-bold text-white shadow-[0_10px_24px_rgba(239,67,67,0.22)] transition hover:bg-[#d93636] active:translate-y-px disabled:cursor-not-allowed disabled:bg-zinc-400"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              {downloading ? t.preparing : t.download}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
